import { Attachment } from '../types';
import { v4 } from 'uuid';
import { initAttachment } from '../Api/Attachments/useAttachmentsHelpers';
import { uploadAttachment } from '../Api/Attachments/attachmentsApi';

// A file can be picked before its Salesforce parent exists, so these hold the upload until one does.
export const stageAttachments = (files: File[]): Attachment[] => files.map((file) => initAttachment(file, v4(), ''));

const RETRYABLE_STATUSES = ['queued', 'error', 'canceled'];

export const isPendingUpload = (attachment: Pick<Attachment, 'status'>): boolean =>
  RETRYABLE_STATUSES.includes(attachment.status);

// Anything the portal still owes Salesforce: in flight, or waiting for the next Submit.
export const isUnsentFile = (attachment: Pick<Attachment, 'status'>): boolean =>
  attachment.status === 'uploading' || isPendingUpload(attachment);

// Refused before any transfer: the portal will not send these, so they are not pending either.
export const refusedFile = (attachment: Attachment): boolean =>
  attachment.status === 'limited' || attachment.status === 'unsupported';

export const uploadStagedAttachments = async (
  attachments: Attachment[],
  parentId: string,
  onUpdate: (attachment: Attachment) => void
): Promise<Attachment[]> => {
  const settled: Attachment[] = [];

  for (const attachment of attachments) {
    if (!isPendingUpload(attachment)) {
      settled.push(attachment);
      continue;
    }

    if (!parentId) {
      const orphaned: Attachment = { ...attachment, status: 'error' };
      onUpdate(orphaned);
      settled.push(orphaned);
      continue;
    }

    // A cancel token is spent once it fires, so a retry needs the fresh one initAttachment mints.
    const uploading: Attachment = { ...initAttachment(attachment.file, attachment.id, parentId), status: 'uploading' };
    onUpdate(uploading);

    // A browser that cannot size the request omits total, and the readout would spell NaN%.
    const progressHandler = ({ loaded, total }: { loaded: number; total?: number }) =>
      total ? onUpdate({ ...uploading, progress: Math.min(Math.round((loaded / total) * 100), 95) }) : undefined;

    const res = await uploadAttachment(uploading, progressHandler, parentId);
    const result: Attachment = res.error
      ? { ...uploading, status: res.error.message === 'canceled' ? 'canceled' : 'error' }
      : { ...uploading, status: 'success', progress: 100 };

    onUpdate(result);
    settled.push(result);
  }

  return settled;
};
